import { type MinItemArray } from '@ag1/ui';
import type { StoreCode } from '@portal/api/shopifyV2/storeCode';
import type { ShopifyProductVariant } from '@portal/api/shopifyV2/types';
import type { PortalOffer } from '@portal/pages/api/offer';
import { LocaleEnum } from '@portal/schema/locale';
import { useLocale } from '@portal/shared/hooks/useLocale';
import { uniqBy } from '@portal/shared/utils/uniqBy';
import { urlFor } from '@portal/shared/utils/urlFor';
import { assertNever } from './helpers';

// Sync with `CHECKOUT_FLOW_TYPES` in Atlas.
export const SUPPORTED_CHECKOUT_FLOW_TYPES = ['bab'] as const;
export type SupportedCheckoutFlowType = (typeof SUPPORTED_CHECKOUT_FLOW_TYPES)[number];
export const isSupportedCheckoutFlowType = (value: string): value is SupportedCheckoutFlowType =>
  SUPPORTED_CHECKOUT_FLOW_TYPES.includes(value as any);

export enum OfferStatus {
  Draft = 'draft',
  Published = 'published',
  Archived = 'archived'
}
export const isOfferStatus = (status: string): status is OfferStatus =>
  Object.values(OfferStatus).includes(status as any);

// Sync with the matching constant in Atlas.
//
// This should only be added to if Atlas actually supports that locale.
export const ValidLanguages = [
  'us',
  'gb',
  'de',
  'nl',
  'se',
  'eu',
  'es',
  'fr',
  'apac-us',
  'au',
  'nz',

  // Not actually supported in Atlas but I don't know if removing it will break
  // anything.
  'jp'
] as const;
export const DefaultLanguage = ValidLanguages[0];
export type Languages = (typeof ValidLanguages)[number];
export const isValidLanguage = (lang: string): lang is Languages =>
  ValidLanguages.includes(lang as Languages);

export enum UserType {
  User = 'user',
  Admin = 'admin'
}

interface User {
  id?: string;
  created_at?: Date;
  type?: UserType;
  display_name?: string;
  email?: string;
  avatar_url?: string;
}

export type Offer = PortalOffer;

export const getOfferAvailabilityRestriction = (
  offer: Offer | null | undefined,
  locale: Languages
) => {
  const restriction = offer?.restriction.find((i) => i.locales?.includes(locale));
  if (!restriction) {
    return null;
  }
  return restriction;
};

export const getOfferAvailabilityRestrictions = (offer: Pick<Offer, 'restriction'>) =>
  uniqBy(
    (offer.restriction || []).filter(
      (i) => i.locales && i.locales.length > 0
    ) as (OfferAvailabilityRestriction & { locales: MinItemArray<Languages> })[],
    (i) => i.locales[0]
  ).sort((a, b) => ValidLanguages.indexOf(a.locales[0]) - ValidLanguages.indexOf(b.locales[0]));

export const getOfferLocales = (offer: Pick<Offer, 'restriction'>) =>
  getOfferAvailabilityRestrictions(offer).map((i) => i.locales[0]);

export interface OfferSubscriptionCustomization {
  valueTitle: string | null;
  subTitle: string | null;
  subDescription: string | null;
  subBullets: string[] | null;
  [key: string]: unknown;
}

export interface OfferSubscription {
  id: number;
  created_at: Date;
  offer_id: number;
  offer_subscription_type_id: number;
  customization: OfferSubscriptionCustomization | null;
  addons: OfferSubscriptionAddon[];
  media: OfferSubscriptionMedia[];
  enable_flavors: boolean;
}

export interface OfferSubscriptionAddon {
  id: number;
  created_at: Date;
  offer_subscription_id: number;
}

export enum OfferSubscriptionMediaType {
  Desktop = 'desktop',
  Mobile = 'mobile'
}

export enum OfferSubscriptionProductType {
  Pouch = 'pouch',
  TravelPack = 'travel-pack'
}

export enum OfferSubscriptionFlavorType {
  Original = 'original',
  Tropical = 'tropical',
  Berry = 'berry'
}

export enum PDPType {
  Bundle = 'bundle',
  Product = 'product'
}

export const isOfferSubscriptionProductType = (
  type: string
): type is OfferSubscriptionProductType =>
  Object.values(OfferSubscriptionProductType).includes(type as any);

export interface OfferSubscriptionMedia {
  id: number;
  created_at: Date;
  offer_subscription_id: number;
  type: OfferSubscriptionMediaType;
  sanity_reference_id: string;
}

export interface OfferAvailabilityRestriction {
  id: number;
  created_at: Date;
  offer_id: number;
  quantity: number | null;
  date_from: Date | null;
  date_to: Date | null;
  locales: Languages[] | null;
  is_sold_out?: boolean;
}
export interface OfferSubscriptionTypeDefaults {
  pouches_default_subscription_type_id: number | null;
  travel_pack_default_subscription_type_id: number | null;
}

export const getLocaleFromAtlas = (atlasLocale: Languages): LocaleEnum => {
  switch (atlasLocale) {
    case 'de':
      return LocaleEnum.deEU;
    case 'eu':
      return LocaleEnum.enEU;
    case 'gb':
      return LocaleEnum.enUK;
    case 'nl':
      return LocaleEnum.nlEU;
    case 'se':
      return LocaleEnum.seEU;
    case 'us':
      return LocaleEnum.enUS;
    case 'es':
      return LocaleEnum.esEU;
    case 'fr':
      return LocaleEnum.frEU;
    case 'jp':
      return LocaleEnum.jaJP;
    case 'apac-us':
      return LocaleEnum.apacUS;
    case 'au':
      return LocaleEnum.enAU;
    case 'nz':
      return LocaleEnum.enNZ;
    default:
      return assertNever(atlasLocale, LocaleEnum.enUS);
  }
};

export const getLocaleForAtlas = (portalLocale: LocaleEnum): Languages => {
  switch (portalLocale) {
    case LocaleEnum.deEU:
    case LocaleEnum.deEud:
    case LocaleEnum.deQa:
      return 'de';
    case LocaleEnum.enEU:
    case LocaleEnum.enEud:
      return 'eu';
    case LocaleEnum.enUK:
    case LocaleEnum.enUkd:
      return 'gb';
    case LocaleEnum.nlEU:
      return 'nl';
    case LocaleEnum.seEU:
      return 'se';
    case LocaleEnum.enUS:
    case LocaleEnum.enDev:
    case LocaleEnum.enQa:
      return 'us';
    case LocaleEnum.esEU:
      return 'es';
    case LocaleEnum.frEU:
      return 'fr';
    case LocaleEnum.enJP:
    case LocaleEnum.jaJP:
      return 'jp';
    case LocaleEnum.apacUS:
      return 'apac-us';
    case LocaleEnum.enAU:
    case LocaleEnum.enAud:
      return 'au';
    case LocaleEnum.enNZ:
    case LocaleEnum.enNzd:
      return 'nz';

    default:
      return assertNever(portalLocale, 'us');
  }
};

export const useLocaleForAtlas = () => {
  const locale = useLocale();
  return getLocaleForAtlas(locale);
};

export type ShopifyVariantsByStoreCode = Partial<Record<StoreCode, ShopifyProductVariant[]>>;

export const getStoreCodeFromAtlasLocale = (locale: Languages): StoreCode => {
  switch (locale) {
    case 'de':
    case 'eu':
    case 'nl':
    case 'se':
    case 'es':
    case 'fr':
      return 'eu';

    case 'gb':
      return 'uk';

    case 'us':
    case 'apac-us':
      return 'us';

    // Has its own store, but uses "us" for now.
    case 'au':
      return 'us'; // 'au';

    // Has its own store, but uses "us" for now.
    case 'nz':
      return 'us'; // 'nz';

    case 'jp':
      return 'jp';

    default:
      return assertNever(locale, 'us');
  }
};

export const getStoreCodeFromLocale = (locale: LocaleEnum): StoreCode => {
  const atlasLocale = getLocaleForAtlas(locale);
  return getStoreCodeFromAtlasLocale(atlasLocale);
};

export const enum InvalidOfferReason {
  SoldOut = 'sold-out',
  Paused = 'paused',
  UnsupportedLocale = 'unsupported-locale',
  Expired = 'expired',
  TooEarly = 'too-early'
}

const OFFER_INVALID_REASON_4O4 = [InvalidOfferReason.UnsupportedLocale] as const;

export type OfferInvalidReason404 = (typeof OFFER_INVALID_REASON_4O4)[number];
export type UnhandledOfferInvalidReason = Exclude<
  InvalidOfferReason,
  (typeof OFFER_INVALID_REASON_4O4)[number]
>;
export const isOfferInvalidReason404 = (reason: string): reason is OfferInvalidReason404 =>
  OFFER_INVALID_REASON_4O4.includes(reason as any);

export const getOfferInvalidReason = (
  offer: Offer,
  locale: LocaleEnum
): InvalidOfferReason | null => {
  const restrictions = getOfferAvailabilityRestriction(offer, getLocaleForAtlas(locale));
  if (!restrictions) {
    return InvalidOfferReason.UnsupportedLocale;
  }

  // TODO(OfferBuilder-v2): Expiry count
  if (restrictions.is_sold_out) {
    return InvalidOfferReason.SoldOut;
  }

  const now = new Date().getTime();
  if (restrictions?.date_to) {
    const expiryDate = new Date(restrictions.date_to).getTime();
    if (expiryDate < now) {
      return InvalidOfferReason.Expired;
    }
  }
  if (restrictions?.date_from) {
    const startDate = new Date(restrictions.date_from).getTime();
    if (now < startDate) {
      return InvalidOfferReason.TooEarly;
    }
  }
  if (offer.is_paused) {
    return InvalidOfferReason.Paused;
  }
  return null;
};

export interface OfferSubscriptionType {
  id: number;
  created_at: Date;
  name: string;
  sort: number;
  locale: Languages;
  product_data: OfferSubscriptionTypeProductData[];
  product_category: OfferSubscriptionProductType;
  media: OfferSubscriptionTypeMedia[];
}

export function getOfferSubscriptionProductData<T extends OfferSubscriptionType>(
  subOrAddon: Pick<T, 'product_data'>
): T['product_data'][number] | null;
export function getOfferSubscriptionProductData<T extends OfferSubscriptionAddonType>(
  subOrAddon: Pick<T, 'product_data'>
): T['product_data'][number] | null;
export function getOfferSubscriptionProductData<
  T extends OfferSubscriptionType | OfferSubscriptionAddonType
>(subOrAddon: Pick<T, 'product_data'>) {
  if (!subOrAddon.product_data || !subOrAddon.product_data.length) {
    return null;
  }
  return subOrAddon.product_data[0];
}

/**
 * Converts applicable mobile/desktop images from a sanity reference to a image
 * source that can be used directly.
 *
 * Provided {@link media} should be pre-filtered to include only the current
 * locale (see {@link Languages}).
 */
export const getOfferSubscriptionMedia = (
  media:
    | Pick<OfferSubscriptionTypeMedia, 'type' | 'sanity_reference_id'>[]
    | Pick<OfferSubscriptionMedia, 'type' | 'sanity_reference_id'>[]
    | Pick<Offer['media'][number], 'type' | 'sanity_reference_id'>[],
  parseToImageSource: boolean
) => {
  let desktop: string | null = null;
  let mobile: string | null = null;

  // eslint-disable-next-line no-restricted-syntax
  for (const item of media) {
    if (item.type === OfferSubscriptionMediaType.Desktop) {
      desktop ||= item.sanity_reference_id;
    } else if (item.type === OfferSubscriptionMediaType.Mobile) {
      mobile ||= item.sanity_reference_id;
    }

    if (desktop && mobile) {
      break;
    }
  }

  if (parseToImageSource) {
    desktop = desktop ? urlFor({ asset: { _ref: desktop, _type: 'reference' } }).url : desktop;
    mobile = mobile ? urlFor({ asset: { _ref: mobile, _type: 'reference' } }).url : mobile;
  }

  return { desktop, mobile };
};

export interface OfferSubscriptionTypeProductData {
  quantity: number;
  isOnetime: boolean;
  productVariantId: string;
}

export interface OfferSubscriptionTypeMedia {
  id: number;
  created_at: Date;
  offer_subscription_type_id: number;
  type: OfferSubscriptionMediaType;
  sanity_reference_id: string;
}

export interface OfferSubscriptionAddonType {
  id: number;
  created_at: Date;
  name: string;
  offer_subscription_type_id: number;
  sort: number;
  locale: Languages;
  product_data: OfferSubscriptionAddonTypeProductData[];
}

export interface OfferSubscriptionAddonTypeProductData {
  quantity: number;
  productVariantId: string;
}

export interface OfferAllowedPages {
  created_at: Date;
  id: number;
  is_active: boolean;
  offer_id: number;
  page_id: number;
}

export interface OfferReview {
  id: number;
  created_at: Date;
  offer_id: number;
  is_active: boolean;
  last_approval_at: Date | null;
}

export interface OfferReviewUser {
  id: number;
  created_at: Date;
  offer_review_id: number;
  user_id: string;
  has_approved: boolean;

  user: User | null;
}
